import * as d3 from 'd3'
import { isMobile } from 'react-device-detect'
import { countries } from 'country-data'
import Chart from '../../ultils/Chart'
import d3tip from '../../ultils/d3-tip'
import './styles.css'
import {
  RISK_STATUS_COLOR_SCALE,
  RISK_STATUS_CONST,
  RISK_TYPE,
} from '../../ultils/chart-d3-utils'

class MarblesChart extends Chart {
  init({ data, redirect, group, isEmptyData, riskType }) {
    let vis = this
    vis.data = data
    vis.riskType = riskType
    vis.originalLength = data.length
    vis.redirect = redirect
    vis.group = group
    vis.isEmptyData = isEmptyData

    vis.tip = d3tip()
      .attr('class', 'd3-tip-marble-chart')
      .html((e, d) => vis.generateTooltipHtml(d))

    vis.openTicker = null

    vis.t = () => d3.transition().duration(500)

    vis.x = d3.scaleBand().domain(RISK_STATUS_CONST).padding(0.3)

    vis.colorScale = RISK_STATUS_COLOR_SCALE

    vis.simulation = d3
      .forceSimulation()
      .force(
        'y',
        d3
          .forceY()
          .strength(0.1)
          .y(vis.HEIGHT / 2 + 16),
      )
      .force('charge', d3.forceManyBody().strength(1)) // Nodes are attracted one each other of value is > 0
      .alphaTarget(0.03)

    vis.dragstarted = function (event, d) {
      if (!event.active) vis.simulation.alphaTarget(0.03).restart()
      d.fx = event.x
      d.fy = event.y
    }
    vis.dragged = function (event, d) {
      d.fx = event.x
      d.fy = event.y
    }
    vis.dragended = function (event, d) {
      if (!event.active) vis.simulation.alphaTarget(0.03)
      d.fx = null
      d.fy = null
    }

    // first time, run by risk for sorting
    vis.update({ group: 'risk' })

    vis.g.call(vis.tip)
  }

  generateTooltipHtml(d) {
    const vis = this
    let returnDiv = `<div class="tip-content">
            <div class="tip-header">
              <div class="tip-title">
                  ${d.proper_name}
              </div>
              <div class="tip-content">
                <div class="row">
                  <div class="row-title">Ticker</div>
                  <div class="row-content" id="ticker-id">${d.ticker}</div>
                </div>
                <div class="row">
                  <div class="row-title">Country</div>
                  <div class="row-content">${
                    countries[d.iso_country].name
                  }</div>
                </div>
                <div class="row">
                  <div class="row-title">Sector</div>
                  <div class="row-content">${d.factset_sector_desc}</div>
                </div>
                <div class="row">
                  <div class="row-title">Status Date</div>
                  <div class="row-content">${d3.timeFormat('%d %B %Y')(
                    new Date(d.date),
                  )}</div>
                </div>
        `
    if (d.bfaiRating) {
      returnDiv += `<div class="row">
                  <div class="row-title">Implied Rating</div>
                  <div class="row-content">${d.bfaiRating}</div>
                </div>`
    }
    returnDiv += `
              </div>
            </div>
          </div>`
    return returnDiv
  }

  update({ group, data, riskType }) {
    let vis = this
    vis.group = group || vis.group
    vis.data = data || vis.data
    vis.riskType = riskType || vis.riskType

    vis.x.range([0, vis.WIDTH])

    vis.simulation.force(
      'collide',
      d3
        .forceCollide()
        .strength(0.05)
        .radius(vis.data.length > 300 ? 20 : vis.data.length < 5 ? 40 : 27)
        .iterations(1),
    ) // Force that avoids circle overlapping

    // sort bubbles
    if (vis.data.length === vis.originalLength) {
      vis.simulation
        .force(
          'x',
          d3
            .forceX()
            .strength(1)
            .x(
              d =>
                vis.x(
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : d.status,
                ) + vis.x.bandwidth(),
            ),
        )
        .alpha(0.5)
    }

    vis.svg.selectAll('image').remove()

    d3.selection.prototype.dblTap = function (callback) {
      let last = 0
      return this.each(function () {
        d3.select(this).on('touchstart', function (e, d) {
          if (e.timeStamp - last < 500) {
            return callback(e, d)
          }
          last = e.timeStamp
        })
      })
    }

    vis.g.selectAll('circle').remove()
    const node = vis.g
      .selectAll('circle')
      .data(vis.data, d => d.ticker)
      .join(
        enter =>
          enter
            .append('circle')
            .on('mouseout', vis.tip.hide)
            .on('mouseover', (e, d) => {
              vis.openTicker = d.ticker
              vis.tip.show(e, d)
            })
            .attr('cx', vis.WIDTH / 2)
            .attr('cy', vis.HEIGHT / 2)
            .attr(
              'fill',
              d =>
                vis.colorScale[
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : d.status
                ] || 'black',
            )
            .attr(
              'stroke',
              d =>
                vis.colorScale[
                  vis.riskType === RISK_TYPE.CREDIT ? d.credit_status : d.status
                ] || 'black',
            )
            .attr('fill-opacity', 0.5)
            .attr('stroke-opacity', 0.7)
            .attr('stroke-width', 4)
            .attr('cursor', 'pointer')
            .on('click', (e, d) => {
              if (!isMobile) {
                vis.tip.hide()
                vis.redirect(`/company/${d.ticker}`)
              }
            })
            .dblTap((e, d) => {
              if (isMobile) {
                vis.tip.hide()
                vis.redirect(`/company/${d.ticker}`)
              }
            })
            .call(
              d3
                .drag()
                .on('start', vis.dragstarted)
                .on('drag', vis.dragged)
                .on('end', vis.dragended),
            )
            .call(enter =>
              enter
                .attr('fill-opacity', 0)
                .attr('stroke-opacity', 0)
                .attr('stroke-width', 0)
                .attr('r', 0)
                .transition(vis.t())
                .attr('fill-opacity', 0.5)
                .attr('stroke-opacity', 0.7)
                .attr(
                  'stroke-width',
                  vis.data.length > 300 ? 2 : vis.data.length < 5 ? 4 : 4,
                )
                .attr(
                  'r',
                  vis.data.length > 300
                    ? 5
                    : vis.data.length < 5
                    ? 20
                    : isMobile
                    ? 8
                    : 10,
                ),
            ),
        update =>
          update.call(update =>
            update
              .transition(vis.t())
              .attr('fill-opacity', 0.5)
              .attr('stroke-opacity', 0.7)
              .attr(
                'stroke-width',
                vis.data.length > 300 ? 2 : vis.data.length < 5 ? 4 : 4,
              )
              .attr(
                'r',
                vis.data.length > 300
                  ? 5
                  : vis.data.length < 5
                  ? 20
                  : isMobile
                  ? 8
                  : 10,
              ),
          ),
        exit =>
          exit.call(exit =>
            exit
              .transition(vis.t())
              .attr('fill-opacity', 0)
              .attr('stroke-opacity', 0)
              .attr('r', 0)
              .attr('stroke-width', 0)
              .remove(),
          ),
      )

    vis.simulation
      .nodes(vis.data, d => d.ticker)
      .on('tick', d =>
        node
          .attr('cx', d => (d.x = Math.max(20, Math.min(vis.WIDTH - 20, d.x))))
          .attr(
            'cy',
            d => (d.y = Math.max(20, Math.min(vis.HEIGHT - 20, d.y))),
          ),
      )

    // wait for circles to sort => then set actual values
    setTimeout(() => {
      vis.simulation
        .force(
          'x',
          d3
            .forceX()
            .strength(vis.group === 'none' ? 0.1 : 0.5)
            .x(
              vis.group === 'none'
                ? vis.WIDTH / 2
                : d =>
                    vis.x(
                      vis.riskType === RISK_TYPE.CREDIT
                        ? d.credit_status
                        : d.status,
                    ) + vis.x.bandwidth(),
            ),
        )
        .force(
          'center',
          vis.group === 'none'
            ? d3.forceCenter(vis.WIDTH / 2, vis.HEIGHT / 2)
            : null,
        )
        .alphaTarget(0.05)
    }, 300)
  }
}

export default MarblesChart
